'use strict';

const libPictView = require('pict-view');

/**
 * CallDesk -- the support intake surface. Pick a customer, see its contacts and recent calls, log a
 * new call, and turn a call into an Issue or a Feedback record (the spawn carries the call's customer
 * + contact onto the new record, server-side). Calls and contacts are plain entities (CallAPI /
 * ContactAPI auto-CRUD); the two spawn routes are CallDeskAPI. Lists are filtered to the chosen
 * customer client-side, which is plenty for support volumes.
 */
class PictViewCallDesk extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._customerID = 0;
	}

	_router() { return this.pict.providers['Pict-Router']; }
	_val(pID) { let tmpEl = (typeof document !== 'undefined') ? document.getElementById(pID) : null; return tmpEl ? String(tmpEl.value || '') : ''; }

	// Load the customer list, then shape + paint. Contacts/calls load when a customer is chosen.
	show()
	{
		let tmpCustAPI = this.pict.providers.KeystoneCustomerAPI;
		if (!tmpCustAPI) { return Promise.resolve(); }
		return tmpCustAPI.list().then(() => { this._shape(); this.render(); });
	}

	/**
	 * Choose the customer whose desk is on screen. NAVIGATES; the route handler renders.
	 *
	 * Picking a customer swaps in that customer's entire working surface -- their contacts, the
	 * add-contact row, the log-a-call form and their call history. None of it could be linked or
	 * returned to, because the whole thing lived in _customerID and never reached the URL.
	 *
	 * The render is _applySelectCustomer, which the route handler calls. This must NOT call that
	 * directly and it must NOT call this, or the route loops.
	 */
	selectCustomer(pIDStr)
	{
		let tmpID = parseInt(pIDStr, 10) || 0;
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showCallDeskCustomer === 'function' && tmpApp.showCallDeskCustomer(tmpID)) { return; }
		return this._applySelectCustomer(tmpID);
	}

	// The render half. Also the entry the /call-desk/:id route lands on.
	_applySelectCustomer(pID)
	{
		this._customerID = parseInt(pID, 10) || 0;
		if (!this._customerID) { this._shape(); this.render(); return; }
		return Promise.all([this.pict.providers.ContactAPI.list(), this.pict.providers.CallAPI.list()])
			.then(() => { this._shape(); this.render(); })
			.catch(() => { this._shape(); this.render(); });
	}

	// Entered from the route with a customer already named: load the customer list first, then that
	// customer's desk, so a cold deep link paints the same page a click does.
	showCustomer(pID)
	{
		let tmpCustAPI = this.pict.providers.KeystoneCustomerAPI;
		if (!tmpCustAPI) { return Promise.resolve(); }
		return tmpCustAPI.list().then(() => this._applySelectCustomer(pID));
	}

	// A reload of the current desk is a render, not a navigation -- the URL already says this.
	reload() { return this._applySelectCustomer(this._customerID); }

	_shape()
	{
		let tmpCustomers = ((this.pict.AppData.Entities && this.pict.AppData.Entities.KeystoneCustomer) || {}).List || [];
		let tmpContacts = ((this.pict.AppData.Entities && this.pict.AppData.Entities.Contact) || {}).List || [];
		let tmpCalls = ((this.pict.AppData.Entities && this.pict.AppData.Entities.Call) || {}).List || [];
		let tmpID = this._customerID;

		let tmpMyContacts = tmpContacts.filter((pC) => (pC.IDKeystoneCustomer || 0) === tmpID);
		let tmpMyCalls = tmpCalls.filter((pC) => (pC.IDKeystoneCustomer || 0) === tmpID)
			.sort((pA, pB) => Date.parse(pB.CallDate || pB.CreateDate || 0) - Date.parse(pA.CallDate || pA.CreateDate || 0));
		this._myCalls = tmpMyCalls;
		let tmpSelected = tmpCustomers.filter((pC) => pC.IDKeystoneCustomer === tmpID)[0] || null;

		this.pict.AppData.CallDesk =
		{
			Customers: tmpCustomers.map((pC) => ({ IDKeystoneCustomer: pC.IDKeystoneCustomer, Name: pC.Name || ('Customer ' + pC.IDKeystoneCustomer), Sel: (pC.IDKeystoneCustomer === tmpID) ? 'selected' : '' })),
			HasCustomer: !!tmpID,
			CustomerName: tmpSelected ? (tmpSelected.Name || '') : '',
			Contacts: tmpMyContacts.map((pC) => ({ IDContact: pC.IDContact, Name: pC.Name || '', Kind: pC.Kind || 'Person', Email: pC.Email || '' })),
			ContactOptions: tmpMyContacts.map((pC) => ({ IDContact: pC.IDContact, Name: (pC.Name || '') + (pC.Kind ? (' (' + pC.Kind + ')') : '') })),
			Calls: tmpMyCalls.map((pC) => ({ IDCall: pC.IDCall, CallLabel: (pC.CallNumber ? ('#' + pC.CallNumber + ' ') : '') + (pC.Subject || '(no subject)'), Channel: pC.Channel || '', Direction: pC.Direction || '', When: pC.CallDate ? String(pC.CallDate).slice(0, 10) : '' })),
			ContactsEmpty: tmpMyContacts.length ? [] : [{ Message: 'No contacts yet. Add the person, shop, or reader you talk to.' }],
			CallsEmpty: tmpMyCalls.length ? [] : [{ Message: 'No calls logged for this customer yet.' }],
			PickPrompt: tmpID ? [] : [{ Message: 'Pick a customer to see their contacts and calls, and to log one.' }],
			BodySlot: tmpID ? [{}] : []
		};
	}

	addContact()
	{
		if (!this._customerID) { return; }
		let tmpName = this._val('cd-contact-name').trim();
		if (!tmpName) { return; }
		let tmpRecord = { IDKeystoneCustomer: this._customerID, Name: tmpName, Kind: this._val('cd-contact-kind') || 'Person', Email: this._val('cd-contact-email').trim() };
		this.pict.providers.ContactAPI.create(tmpRecord).then(() => this.reload()).catch(() => { /* ignore */ });
	}

	saveCall()
	{
		if (!this._customerID) { return; }
		let tmpSubject = this._val('cd-call-subject').trim();
		if (!tmpSubject) { return; }
		let tmpRecord =
		{
			IDKeystoneCustomer: this._customerID,
			IDContact: parseInt(this._val('cd-call-contact'), 10) || 0,
			Channel: this._val('cd-call-channel') || 'Phone',
			Direction: this._val('cd-call-direction') || 'Inbound',
			Subject: tmpSubject,
			Notes: this._val('cd-call-notes'),
			CallDate: (new Date()).toISOString()
		};
		this.pict.providers.CallAPI.create(tmpRecord).then(() => this.reload()).catch(() => { /* ignore */ });
	}

	_modal() { return this.pict.views['Pict-Section-Modal']; }
	_esc(pStr) { return String(pStr).replace(/[&<>"]/g, (pCh) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pCh])); }
	_callSubject(pCallID) { let tmpCall = (this._myCalls || []).find((pC) => pC.IDCall === pCallID) || {}; return tmpCall.Subject || ''; }
	_field(pLabel, pControl) { return '<div style="margin-bottom:9px;"><label style="font-size:12px;color:var(--theme-color-text-secondary,#5b6376);">' + pLabel + '</label>' + pControl + '</div>'; }

	// Turn a call into an issue: a small form sets the type, priority, and severity (title prefilled
	// from the call), then spawn carries the call's customer + contact onto the new issue server-side.
	makeIssue(pCallID)
	{
		let tmpModal = this._modal();
		let tmpRouter = this._router();
		if (!tmpModal || typeof tmpModal.show !== 'function') { return this.pict.providers.CallDeskAPI.spawnIssue(pCallID, {}).then((pR) => { if (pR && pR.IDIssue && tmpRouter) { tmpRouter.navigate('/issue/' + pR.IDIssue); } }); }
		let tmpStyle = 'width:100%;box-sizing:border-box;padding:6px 8px;font:inherit;margin-top:2px;';
		let fSel = (pVals, pDefault) => pVals.map((pV) => '<option' + (pV === pDefault ? ' selected' : '') + '>' + pV + '</option>').join('');
		this.pict.providers.IssueTypeAPI.list().then((pTypes) =>
		{
			let tmpTypeOpts = '<option value="0">(no type)</option>' + (pTypes || []).map((pT) => '<option value="' + pT.IDIssueType + '">' + this._esc(pT.Name) + '</option>').join('');
			tmpModal.show(
			{
				title: 'New issue from call',
				content:
					this._field('Title', '<input id="cd-issue-title" type="text" style="' + tmpStyle + '" value="' + this._esc(this._callSubject(pCallID)) + '">') +
					this._field('Type', '<select id="cd-issue-type" style="' + tmpStyle + '">' + tmpTypeOpts + '</select>') +
					this._field('Priority', '<select id="cd-issue-priority" style="' + tmpStyle + '">' + fSel(['Low', 'Normal', 'High', 'Urgent'], 'Normal') + '</select>') +
					this._field('Severity', '<select id="cd-issue-severity" style="' + tmpStyle + '"><option value="">(none)</option>' + fSel(['Minor', 'Major', 'Critical'], '') + '</select>'),
				buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Create issue', Style: 'primary' }]
			}).then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let fVal = (pID) => { let tmpEl = (typeof document !== 'undefined') ? document.getElementById(pID) : null; return tmpEl ? tmpEl.value : ''; };
				let tmpDraft = { Title: fVal('cd-issue-title').trim(), IDIssueType: parseInt(fVal('cd-issue-type'), 10) || 0, Priority: fVal('cd-issue-priority') || 'Normal', Severity: fVal('cd-issue-severity') };
				this.pict.providers.CallDeskAPI.spawnIssue(pCallID, tmpDraft).then((pResult) =>
				{
					if (pResult && pResult.IDIssue && tmpRouter) { tmpRouter.navigate('/issue/' + pResult.IDIssue); }
				}).catch(() => { /* ignore */ });
			});
		});
	}

	// Turn a call into a feedback record: a small form sets the narrative and the three likert scores.
	makeFeedback(pCallID)
	{
		let tmpModal = this._modal();
		let tmpRouter = this._router();
		if (!tmpModal || typeof tmpModal.show !== 'function') { return this.pict.providers.CallDeskAPI.spawnFeedback(pCallID, {}).then((pR) => { if (pR && pR.IDFeedback && tmpRouter) { tmpRouter.navigate('/feedback/' + pR.IDFeedback); } }); }
		let tmpStyle = 'width:100%;box-sizing:border-box;padding:6px 8px;font:inherit;margin-top:2px;';
		let tmpScore = '<option value="0">unscored</option>' + [1, 2, 3, 4, 5].map((pN) => '<option>' + pN + '</option>').join('');
		tmpModal.show(
		{
			title: 'New feedback from call',
			content:
				this._field('Title', '<input id="cd-fb-title" type="text" style="' + tmpStyle + '" value="' + this._esc(this._callSubject(pCallID)) + '">') +
				this._field('What they said', '<textarea id="cd-fb-body" style="' + tmpStyle + 'min-height:60px;resize:vertical;"></textarea>') +
				this._field('Did what was expected', '<select id="cd-fb-s1" style="' + tmpStyle + '">' + tmpScore + '</select>') +
				this._field('Intuitive to use', '<select id="cd-fb-s2" style="' + tmpStyle + '">' + tmpScore + '</select>') +
				this._field('Enjoyed it', '<select id="cd-fb-s3" style="' + tmpStyle + '">' + tmpScore + '</select>'),
			buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Create feedback', Style: 'primary' }]
		}).then((pChoice) =>
		{
			if (pChoice !== 'ok') { return; }
			let fVal = (pID) => { let tmpEl = (typeof document !== 'undefined') ? document.getElementById(pID) : null; return tmpEl ? tmpEl.value : ''; };
			let tmpDraft = { Title: fVal('cd-fb-title').trim(), Body: fVal('cd-fb-body'), ScoreDidWhatExpected: parseInt(fVal('cd-fb-s1'), 10) || 0, ScoreIntuitive: parseInt(fVal('cd-fb-s2'), 10) || 0, ScoreEnjoyment: parseInt(fVal('cd-fb-s3'), 10) || 0 };
			this.pict.providers.CallDeskAPI.spawnFeedback(pCallID, tmpDraft).then((pResult) =>
			{
				if (pResult && pResult.IDFeedback && tmpRouter) { tmpRouter.navigate('/feedback/' + pResult.IDFeedback); }
			}).catch(() => { /* ignore */ });
		});
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewCallDesk;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-CallDesk',
	DefaultRenderable: 'CallDesk-Display',
	AutoRender: false,
	CSS: /*css*/`
		.ps-calldesk { height: 100%; display: flex; flex-direction: column; gap: 14px; overflow-y: auto; }
		.ps-cd-head { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
		.ps-cd-head h2 { font-size: 22px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); margin: 0; }
		.ps-cd-pick { font: inherit; font-size: 14px; padding: 6px 10px; border-radius: 7px; border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); min-width: 240px; }
		.ps-cd-prompt { color: var(--theme-color-text-muted, #97a1ab); font-size: 15px; padding: 30px 4px; }
		.ps-cd-cols { display: flex; gap: 16px; align-items: flex-start; flex-wrap: wrap; }
		.ps-cd-panel { flex: 1 1 340px; background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 12px; padding: 16px 18px; }
		.ps-cd-panel h3 { font-size: 14px; font-weight: 700; color: var(--theme-color-text-primary, #2c3140); margin: 0 0 12px; }
		.ps-cd-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; }
		.ps-cd-field label { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-cd-field input, .ps-cd-field select, .ps-cd-field textarea { font: inherit; font-size: 14px; padding: 7px 9px; border-radius: 7px; border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #2c3140); }
		.ps-cd-field textarea { min-height: 56px; resize: vertical; }
		.ps-cd-row3 { display: flex; gap: 8px; }
		.ps-cd-row3 > * { flex: 1; }
		.ps-cd-btn { font: inherit; font-size: 13px; border: 1px solid var(--theme-color-brand-primary, #2880a6); background: var(--theme-color-brand-primary, #2880a6); color: var(--theme-color-text-on-brand, #fff); border-radius: 7px; padding: 7px 14px; cursor: pointer; }
		.ps-cd-btn:hover { filter: brightness(1.06); }
		.ps-cd-list { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
		.ps-cd-contact { display: flex; align-items: baseline; gap: 8px; font-size: 14px; color: var(--theme-color-text-primary, #2c3140); padding: 5px 0; border-bottom: 1px solid var(--theme-color-border-light, #f0f3f6); }
		.ps-cd-contact .k { font-size: 11px; padding: 1px 7px; border-radius: 999px; background: var(--theme-color-background-secondary, #eef1f6); color: var(--theme-color-text-secondary, #5b6376); }
		.ps-cd-contact .e { color: var(--theme-color-text-muted, #97a1ab); font-size: 12px; margin-left: auto; }
		.ps-cd-call { border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 9px; padding: 9px 11px; }
		.ps-cd-call-top { display: flex; align-items: baseline; gap: 8px; margin-bottom: 6px; }
		.ps-cd-call-subj { font-size: 14px; color: var(--theme-color-text-primary, #2c3140); }
		.ps-cd-call-meta { font-size: 11px; color: var(--theme-color-text-muted, #97a1ab); margin-left: auto; }
		.ps-cd-call-acts { display: flex; gap: 8px; }
		.ps-cd-mini { font: inherit; font-size: 12px; border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-secondary, #eef1f6); color: var(--theme-color-text-secondary, #41506a); border-radius: 6px; padding: 4px 10px; cursor: pointer; }
		.ps-cd-mini:hover { background: var(--theme-color-background-hover, #e1e7ef); }
		.ps-cd-empty { color: var(--theme-color-text-muted, #97a1ab); font-size: 13px; padding: 8px 0; }
		.ps-cd-addrow { display: flex; gap: 8px; align-items: flex-end; margin-top: 10px; flex-wrap: wrap; }
		.ps-cd-addrow .ps-cd-field { margin-bottom: 0; }
	`,
	Templates:
	[
		{
			Hash: 'CallDesk-Display',
			Template: /*html*/`
<div class="ps-page ps-calldesk">
	<div class="ps-cd-head">
		<h2>Call Desk</h2>
		<select class="ps-cd-pick" onchange="_Pict.views['Plansheet-CallDesk'].selectCustomer(this.value)">
			<option value="0">Choose a customer...</option>
			{~TS:CallDesk-CustomerOption:AppData.CallDesk.Customers~}
		</select>
	</div>
	{~TS:CallDesk-Prompt:AppData.CallDesk.PickPrompt~}
	{~TS:CallDesk-Body:AppData.CallDesk.BodySlot~}
</div>`
		},
		{
			Hash: 'CallDesk-CustomerOption',
			Template: /*html*/`<option value="{~D:Record.IDKeystoneCustomer~}" {~D:Record.Sel~}>{~D:Record.Name~}</option>`
		},
		{
			Hash: 'CallDesk-Prompt',
			Template: /*html*/`<div class="ps-cd-prompt">{~D:Record.Message~}</div>`
		},
		{
			Hash: 'CallDesk-Body',
			Template: /*html*/`
<div class="ps-cd-cols">
	<div class="ps-cd-panel">
		<h3>Contacts at {~D:AppData.CallDesk.CustomerName~}</h3>
		<div class="ps-cd-list">
			{~TS:CallDesk-Contact:AppData.CallDesk.Contacts~}
			{~TS:CallDesk-Empty:AppData.CallDesk.ContactsEmpty~}
		</div>
		<div class="ps-cd-addrow">
			<div class="ps-cd-field"><label>Name</label><input id="cd-contact-name" type="text" placeholder="Person, shop, or reader"></div>
			<div class="ps-cd-field"><label>Kind</label><select id="cd-contact-kind"><option>Person</option><option>Shop</option><option>Reader</option><option>Org</option></select></div>
			<div class="ps-cd-field"><label>Email</label><input id="cd-contact-email" type="text" placeholder="optional"></div>
			<button class="ps-cd-mini" onclick="_Pict.views['Plansheet-CallDesk'].addContact()">Add contact</button>
		</div>
	</div>
	<div class="ps-cd-panel">
		<h3>Log a call</h3>
		<div class="ps-cd-field"><label>Contact</label><select id="cd-call-contact"><option value="0">(unspecified)</option>{~TS:CallDesk-ContactOpt:AppData.CallDesk.ContactOptions~}</select></div>
		<div class="ps-cd-row3">
			<div class="ps-cd-field"><label>Channel</label><select id="cd-call-channel"><option>Phone</option><option>Email</option><option>Chat</option><option>InPerson</option><option>Other</option></select></div>
			<div class="ps-cd-field"><label>Direction</label><select id="cd-call-direction"><option>Inbound</option><option>Outbound</option></select></div>
		</div>
		<div class="ps-cd-field"><label>Subject</label><input id="cd-call-subject" type="text" placeholder="What was the call about?"></div>
		<div class="ps-cd-field"><label>Notes</label><textarea id="cd-call-notes" placeholder="What did they say?"></textarea></div>
		<button class="ps-cd-btn" onclick="_Pict.views['Plansheet-CallDesk'].saveCall()">Save call</button>
		<div class="ps-cd-list">
			{~TS:CallDesk-Call:AppData.CallDesk.Calls~}
			{~TS:CallDesk-Empty:AppData.CallDesk.CallsEmpty~}
		</div>
	</div>
</div>`
		},
		{
			Hash: 'CallDesk-Contact',
			Template: /*html*/`<div class="ps-cd-contact"><span>{~D:Record.Name~}</span><span class="k">{~D:Record.Kind~}</span><span class="e">{~D:Record.Email~}</span></div>`
		},
		{
			Hash: 'CallDesk-ContactOpt',
			Template: /*html*/`<option value="{~D:Record.IDContact~}">{~D:Record.Name~}</option>`
		},
		{
			Hash: 'CallDesk-Call',
			Template: /*html*/`
<div class="ps-cd-call">
	<div class="ps-cd-call-top"><span class="ps-cd-call-subj">{~D:Record.CallLabel~}</span><span class="ps-cd-call-meta">{~D:Record.Channel~} {~D:Record.Direction~} {~D:Record.When~}</span></div>
	<div class="ps-cd-call-acts">
		<button class="ps-cd-mini" onclick="_Pict.views['Plansheet-CallDesk'].makeIssue({~D:Record.IDCall~})">Make an issue</button>
		<button class="ps-cd-mini" onclick="_Pict.views['Plansheet-CallDesk'].makeFeedback({~D:Record.IDCall~})">Make feedback</button>
	</div>
</div>`
		},
		{
			Hash: 'CallDesk-Empty',
			Template: /*html*/`<div class="ps-cd-empty">{~D:Record.Message~}</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'CallDesk-Display',
			TemplateHash: 'CallDesk-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
